import React, { Component } from 'react';
import { connect } from 'react-redux';
import { string, func, shape, arrayOf, bool, number, object, oneOfType } from 'prop-types';

import shapeStore from '~constants/propTypes';
import { googleAddressPropTypes } from '~propTypes/address';
import { actionCreators as actions } from '~redux/SearchStore/actions';
import { actionCreators as tabActions } from '~components/Tabs/redux/actions';
import {
  NEAR_TARGET,
  DELIVERY_TARGET,
  SELECTED_DELIVERY_TIME,
  CURRENT_SUBSIDIARY_TARGET
} from '~redux/SearchStore/constants';
import { DISPATCH_METHODS } from '~constants/address';
import { normalizeCoordinates } from '~utils/maps';
import LocalStorageService from '~services/LocalStorageService';
import { EVENT, sendGTMEvent } from '~utils/analytics';

import DeliveryOrPickUp from './layout';

const base64 = require('~utils/base64');

class DeliveryOrPickUpContainer extends Component {
  state = {
    selectedStore: null,
    scheduled: false
  };

  componentDidMount() {
    const { setTargetValue, selectedAddress, currentSubsidiary } = this.props;
    const fakeStoreId = LocalStorageService.getFakeStoreId();
    this.setState({ selectedStore: currentSubsidiary });
    setTargetValue(`${NEAR_TARGET}Loading`, false);
    setTargetValue(`${DELIVERY_TARGET}Loading`, false);

    if (fakeStoreId !== selectedAddress?.id) {
      this.findNearbyStores();
    }
  }

  sendGoogleEvent() {
    const { selectedAddress, deliveryError } = this.props;
    if (deliveryError === 'CLIENT_ERROR') {
      sendGTMEvent(EVENT.deliveryOutReach, {
        channel: 'delivery',
        address: 'not valid',
        // eslint-disable-next-line camelcase
        street_address: base64.encode(selectedAddress?.formattedAddress || '')
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { stores } = this.props;
    const { selectedStore } = this.state;
    if (stores && selectedStore) {
      const existsSavedStoreInNewStores = stores.find(store => store.id === selectedStore.id);
      if (!existsSavedStoreInNewStores) {
        this.setState({ selectedStore: null });
      }
    }
    if (prevProps.deliveryError !== this.props.deliveryError) {
      this.sendGoogleEvent();
    }
    if (prevProps.selectedAddress !== this.props.selectedAddress) {
      this.findNearbyStores();
    }
  }

  componentWillUnmount() {
    const { setTargetValue } = this.props;

    setTargetValue(`${NEAR_TARGET}Loading`, true);
    setTargetValue(`${DELIVERY_TARGET}Loading`, true);
  }

  findNearbyStores = () => {
    const { getNearLocation, getStoreDeliveryInfo, selectedAddress } = this.props;
    if (selectedAddress) {
      const location = normalizeCoordinates(selectedAddress);
      getNearLocation(location);
      const addressObject = {
        formattedAddress: selectedAddress.formattedAddress,
        textAddress: selectedAddress.textAddress,
        commune: selectedAddress.commune,
        region: selectedAddress.region
      };
      getStoreDeliveryInfo(location, addressObject);
    }
  };

  setScheduled = scheduled => this.setState({ scheduled });

  handleStoreSelect = ({ store }) => {
    this.setState({ selectedStore: store });
  };

  handleConfirm = ({ store, addressDetails, type, hotspot, papaCar }) => {
    const { onConfirm } = this.props;
    const { selectedStore } = this.state;
    let dispatchMethod = type;
    if (hotspot && !papaCar) {
      dispatchMethod = DISPATCH_METHODS.PJ_DELIVERY;
    } else if (papaCar) {
      dispatchMethod = DISPATCH_METHODS.IN_STORE;
    }

    onConfirm({
      store: selectedStore || store,
      addressDetails,
      type: dispatchMethod,
      hotspot,
      papaCar
    });
  };

  render() {
    const {
      loading,
      stores,
      storesError,
      selectedAddress,
      selectedDeliveryTime,
      delivery,
      deliveryError,
      onDispatchChange,
      changeTab,
      onCleanSearchInput,
      createUserAddress,
      editUserAddress,
      setSelectedDeliveryTime,
      onAddressSelect,
      incompleteAddress,
      onSearchInputChange,
      onMarkerDragEnd
    } = this.props;
    const { selectedStore, scheduled } = this.state;

    return (
      <DeliveryOrPickUp
        selectedAddress={selectedAddress}
        stores={stores}
        storesError={storesError}
        delivery={delivery}
        deliveryError={deliveryError}
        selectedStore={selectedStore}
        selectedDeliveryTime={selectedDeliveryTime}
        onStoreSelect={this.handleStoreSelect}
        onConfirm={this.handleConfirm}
        onDispatchChange={onDispatchChange}
        loadingStores={loading}
        changeTab={changeTab}
        onCleanSearchInput={onCleanSearchInput}
        createUserAddress={createUserAddress}
        editUserAddress={editUserAddress}
        setSelectedDeliveryTime={setSelectedDeliveryTime}
        onAddressSelect={onAddressSelect}
        incompleteAddress={incompleteAddress}
        onSearchInputChange={onSearchInputChange}
        onMarkerDragEnd={onMarkerDragEnd}
        scheduled={scheduled}
        setScheduled={this.setScheduled}
      />
    );
  }
}

DeliveryOrPickUpContainer.propTypes = {
  getNearLocation: func.isRequired,
  getStoreDeliveryInfo: func.isRequired,
  setSelectedDeliveryTime: func.isRequired,
  setTargetValue: func.isRequired,
  onAddressSelect: func.isRequired,
  onConfirm: func.isRequired,
  changeTab: func,
  createUserAddress: func,
  currentSubsidiary: object,
  delivery: shape({
    name: string,
    deliveryWaitTime: number,
    menuId: number
  }),
  deliveryError: string,
  editUserAddress: func,
  incompleteAddress: oneOfType([string, undefined]),
  loading: bool,
  selectedAddress: googleAddressPropTypes,
  selectedDeliveryTime: object,
  stores: arrayOf(shapeStore),
  storesError: string,
  onCleanSearchInput: func,
  onDispatchChange: func,
  onMarkerDragEnd: func,
  onSearchInputChange: func
};

const mapStateToProps = state => ({
  delivery: state.searchStore[DELIVERY_TARGET],
  deliveryError: state.searchStore[`${DELIVERY_TARGET}Error`],
  selectedDeliveryTime: state.searchStore[SELECTED_DELIVERY_TIME],
  stores: state.searchStore[NEAR_TARGET],
  currentSubsidiary: state.searchStore[CURRENT_SUBSIDIARY_TARGET],
  storesError: state.searchStore[`${NEAR_TARGET}Error`],
  loading: state.searchStore[`${NEAR_TARGET}Loading`] || state.searchStore[`${DELIVERY_TARGET}Loading`]
});

const mapDispatchToProps = dispatch => ({
  changeTab: newTabId => dispatch(tabActions.changeTab(newTabId)),
  getNearLocation: location => dispatch(actions.getNear(location)),
  getStoreDeliveryInfo: (location, addressObject) =>
    dispatch(actions.getStoreDelivery(location, addressObject)),
  setTargetValue: (target, payload) => dispatch(actions.setValue(target, payload)),
  setSelectedDeliveryTime: data => dispatch(actions.setSelectedDeliveryTime(data)),
  createUserAddress: address => dispatch(actions.createUserAddress(address)),
  editUserAddress: (address, id) => dispatch(actions.editUserAddress(address, id))
});

export default connect(mapStateToProps, mapDispatchToProps)(DeliveryOrPickUpContainer);
